import React, { useEffect, useState } from 'react'
import { CircularProgress, TableBody, TableCell, TableRow } from '@material-ui/core'
import { userProfile } from '@views/profile/state/profileSlice'
import { compareAsc, compareDesc, isValid } from 'date-fns'
import { useDispatch, useSelector } from 'react-redux'
import { Order } from '.'
import { Role } from '@types'
import { patchStages, sendSuccess, trackingLoader } from '@views/tracking/state/trackingSlice2'
import BoxComponent from '@views/tracking/components/BoxComponent'
import TableCellComponent from '@views/tracking/components/TableCellComponent'
import EditStagesModal from '@views/tracking/modals/EditStagesModal'
import SeeIconModal from '@views/tracking/modals/SeeIconModal'
import ModalIcon from '@views/tracking/modals/ModalIcon'
import { TrackingRows } from '@types'

interface Props {
  users: TrackingRows[]
  isNumeric: boolean
  order: Order
  orderBy: string
  rowsPerPage: number
  page: number
  isDateSort: boolean
  isLoading: boolean
  isTeam: boolean
}

const TrackingBody: React.FC<Props> = ({
  users,
  isNumeric,
  order,
  orderBy,
  rowsPerPage,
  page,
  isDateSort,
  isLoading,
  isTeam
}) => {
  const dispatch = useDispatch()
  const { isSuccess } = useSelector(trackingLoader)
  const { role } = useSelector(userProfile)
  const [showModal, setShowModal] = useState(false)
  const [selectedRow, setSelectedRow] = useState<TrackingRows>()

  useEffect(() => {
    if (isSuccess) setShowModal(false)
  }, [isSuccess])

  const openEditStages = () => {
    if (role === Role.admin || role === Role.operaciones) {
      setShowModal(true)
    }
  }

  const currencyFormat = (num: number) => {
    return `$ ${num.toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,')}`
  }

  const backgroundColor = (row: TrackingRows) => {
    const colors = {
      yellow: '#ffe22d',
      red: 'rgb(209 6 6)',
      white: 'white',
      green: '#1cbe37c7',
      orange: '#ff8530'
    }
    return row?.isRejected
      ? colors.red
      : row.studyDate !== '' && row.office === '' && row.bank === '' && row.success === ''
      ? colors.white
      : row.office !== '' && row.studyDate !== '' && row.bank === '' && row.success === ''
      ? colors.yellow
      : row.bank !== '' && row.studyDate !== '' && row.office !== '' && row.success === ''
      ? colors.green
      : row.success !== ''
      ? colors.orange
      : colors.white
  }
  if (isLoading)
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '60vh',
          width: '100vw'
        }}
      >
        <CircularProgress style={{ color: '#ff6900' }} />
      </div>
    )
  return (
    <>
      <TableBody>
        {users
          .sort((itemA, itemB) => {
            const a = itemA.createdAt
            const b = itemB.createdAt
            const dateA = isValid(new Date(a)) ? new Date(a) : a ? new Date('1990/01/01') : new Date()
            const dateB = isValid(new Date(b)) ? new Date(b) : b ? new Date('1990/01/01') : new Date()
            return compareDesc(dateA, dateB)
          })
          .sort((itemA: any, itemB: any) => {
            const a = itemA[orderBy]
            const b = itemB[orderBy]
            try {
              if (isNumeric) {
                if (order === 'asc') return b - a
                else return a - b
              } else if (isDateSort) {
                const dateA = isValid(new Date(a)) ? new Date(a) : a ? new Date('1990/01/01') : new Date()
                const dateB = isValid(new Date(b)) ? new Date(b) : b ? new Date('1990/01/01') : new Date()
                if (order === 'asc') {
                  return compareAsc(dateA, dateB)
                } else {
                  return compareDesc(dateA, dateB)
                }
              } else {
                if (order === 'asc') return b.localeCompare(a)
                else return a.localeCompare(b)
              }
            } catch (error) {
              console.log(error)
            }
          })
          .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
          .map(row => {
            return (
              <TableRow
                style={{
                  background: backgroundColor(row)
                }}
                className="table-row--container"
                key={row.id}
                onClick={() => setSelectedRow(row)}
              >
                <TableCell align="left">
                  <BoxComponent fontSize="15px" selected={row.name.toUpperCase()} />
                  <BoxComponent fontSize="15px" selected={`${row.phone} - ${row.createdAt.split('T')[0]}`} />
                  <BoxComponent fontSize="13px" selected={row.parentName} />
                  <BoxComponent fontSize="12px" selected={row.executive?.name?.toUpperCase() || ''} />
                </TableCell>
                <TableCell align="left">{row.bankName}</TableCell>
                <TableCellComponent row={row} openEditStages={openEditStages}>
                  {row.studyDate}
                </TableCellComponent>
                <TableCellComponent row={row} openEditStages={openEditStages}>
                  {row.office}
                </TableCellComponent>
                <TableCellComponent row={row} openEditStages={openEditStages}>
                  {row.bank}
                </TableCellComponent>
                <TableCellComponent row={row} openEditStages={openEditStages}>
                  {row.success}
                </TableCellComponent>
                <TableCellComponent row={row} openEditStages={openEditStages}>
                  {row.fee > 0 ? currencyFormat(row.fee) : ''}
                </TableCellComponent>
                <TableCellComponent row={row} openEditStages={openEditStages}>
                  {row.fee > 0 ? currencyFormat(Math.ceil(row.fee / 1.19)) : ''}
                </TableCellComponent>

                <TableCell>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}
                  >
                    <ModalIcon row={row} role={role} isAddEnable={false} />
                    {row.id ? <SeeIconModal userId={row.id} isTeam={isTeam}></SeeIconModal> : null}
                  </div>
                </TableCell>
              </TableRow>
            )
          })}
      </TableBody>
      <EditStagesModal
        selectedRow={selectedRow}
        isOpen={showModal}
        onCancel={() => setShowModal(false)}
        onSave={(stages, extraFields, isRejected, isReturned, successForm, hasChangedSuccess) => {
          if (selectedRow?.id) {
            setShowModal(false)
            dispatch(
              patchStages({
                stages,
                isRejected,
                isReturned,
                userId: selectedRow.id,
                isTeam,
                successForm,
                extraFields,
                hasChangedSuccess
              })
            )
          }
        }}
        onSuccess={() => dispatch(sendSuccess(selectedRow?.id || ''))}
      />
    </>
  )
}

export default TrackingBody
